/**
 * BOM Configuration Key Constants
 *
 * These constants define the valid keys for each BOM configuration entity type.
 * Keys correspond to static values used in the BOM generation code and should not
 * be modified without updating the corresponding BOM generation logic.
 */

// ===== ROUTING KEYS =====
export const BOM_ROUTING_KEYS = [
  'DTC',
  'DTC_PEX',
  'DTC_PEX_FLEX',
  'DTC_PEX_PANEL_BOX',
  'DTC_PEX_FLEX_PANEL_BOX',
  'DTC_PEX_SAND',
  'DTC_PEX_FLEX_SAND',
  'DTC_HDPE',
  'DTC_HDPE_FLEX',
  'DTC_HDPE_PANELBOX',
  'DTC_HDPE_FLEX_PANELBOX',
  'DTC_STAINLESS',
  'DTC_STAINLESS_FLEX',
  'RIGID_PROBE',
  'RIGID_PROBE_FLEX',
  'SDM_PROBE',
  'DTC_MLINK_STAINLESS',
  'DTC_MLINK_PEX',
  'DTC_MLINK',
  'AIR_TEMP',
] as const

export type BomRoutingKey = (typeof BOM_ROUTING_KEYS)[number]

// ===== MAPPING KEYS =====
// Mapping keys are the same as routing keys
export const BOM_MAPPING_KEYS = BOM_ROUTING_KEYS

export type BomMappingKey = BomRoutingKey

// ===== CONDUIT TRANSITION KEYS =====
export const BOM_CONDUIT_TRANSITION_KEYS = [
  'START_FLEX',
  'END_FLEX',
  'FLEX_TO_PEX',
  'FLEX_TO_PEX_SAND',
  'FLEX_TO_HDPE',
  'START_PEX',
  'END_PEX',
  'PEX_TO_FLEX',
  'START_PEX_SAND',
  'END_PEX_SAND',
  'PEX_SAND_TO_FLEX',
  'START_HDPE',
  'END_HDPE',
  'HDPE_TO_FLEX',
  'RIGID_PROBE',
  'PEX_TO_FLEX_TO_PANELBOX',
  'HDPE_TO_FLEX_TO_PANELBOX',
] as const

export type BomConduitTransitionKey =
  (typeof BOM_CONDUIT_TRANSITION_KEYS)[number]

// ===== PART CATEGORIES =====
export const BOM_PART_CATEGORIES = [
  'packaging',
  'heatshrink',
  'connector',
  'mold',
  'moldColor',
  'cableColor',
  'conduit',
  'board',
  'airtemp',
  'standardDtc',
] as const

export type BomPartCategory = (typeof BOM_PART_CATEGORIES)[number]

// ===== PART KEYS BY CATEGORY =====
export const BOM_PART_KEYS_BY_CATEGORY = {
  packaging: ['spool'] as const,
  heatshrink: ['black', 'white'] as const,
  connector: ['RAYMO', 'XLR', 'BARE', 'SUBCONN'] as const,
  mold: [
    'straight',
    'none',
    '90degree',
    'end',
    'mlink',
    'xlr',
    '9mm',
    '11mm',
    '14mm',
  ] as const,
  moldColor: ['blueA', 'blueB'] as const,
  cableColor: [
    'Standard Cable',
    'Armored Cable',
    'White Cable',
    'Red Cable',
    'Wires',
  ] as const,
  conduit: [
    'Flex',
    'Pex',
    'Pex with Sand',
    'HDPE',
    'Stainless',
    'StainlessProbe',
  ] as const,
  board: ['mlinkBoard', 'sensorBoard', 'protectionBoard'] as const,
  airtemp: [
    'radshield',
    'sensor',
    'heatshrink',
    'label',
    'clearHeatshrink',
  ] as const,
  standardDtc: ['labels', 'clearHeatshrink'] as const,
} as const satisfies Record<BomPartCategory, readonly string[]>

// Type for part keys (union of all possible keys)
export type BomPartKey =
  (typeof BOM_PART_KEYS_BY_CATEGORY)[BomPartCategory][number]

// Helper function to get keys for a specific category
export function getPartKeysForCategory(
  category: BomPartCategory,
): readonly string[] {
  return BOM_PART_KEYS_BY_CATEGORY[category] ?? []
}

// Helper to check if a key is valid for a category
export function isValidPartKey(
  category: BomPartCategory,
  key: string,
): boolean {
  const validKeys = BOM_PART_KEYS_BY_CATEGORY[category] as readonly string[]
  return validKeys?.includes(key) ?? false
}

// Helper to check if a routing key is valid
export function isValidRoutingKey(key: string): key is BomRoutingKey {
  return BOM_ROUTING_KEYS.includes(key as BomRoutingKey)
}

// Helper to check if a mapping key is valid
export function isValidMappingKey(key: string): key is BomMappingKey {
  return BOM_MAPPING_KEYS.includes(key as BomMappingKey)
}

// Helper to check if a conduit transition key is valid
export function isValidConduitTransitionKey(
  key: string,
): key is BomConduitTransitionKey {
  return BOM_CONDUIT_TRANSITION_KEYS.includes(key as BomConduitTransitionKey)
}
